import type { WealthicaConnection } from '../../types';
import { toast } from 'sonner';
import { deleteAccountsAndPositionsFromDatabase } from '../../services/WealthicaPersistenceService';

// Local helper types to avoid leaking store internals
interface StoreState {
  isPersisting?: boolean;
  processingState?: string;
  persistenceError?: string | null;
}

type SetFn = (partial: Partial<StoreState> | ((state: StoreState) => Partial<StoreState>)) => void;

type UnknownRecord = Record<string, unknown>;
const asRecord = (u: unknown): UnknownRecord => (u && typeof u === 'object' ? (u as UnknownRecord) : {});
const getNumber = (u: unknown): number | undefined => (typeof u === 'number' ? u : undefined);

export async function deleteAccountDataImpl(
  set: unknown,
  _get: unknown,
  userId: string,
  connectionData: WealthicaConnection,
  preserveConnectionOrOptions: boolean | { preserveConnection?: boolean; deleteMode?: 'stop_syncing' | 'full_delete'; suppressSuccessToast?: boolean } = false
): Promise<void> {
  const _set = set as SetFn;

  _set({ isPersisting: true, processingState: 'processing', persistenceError: null });

  try {
    const result = await deleteAccountsAndPositionsFromDatabase(userId, connectionData, preserveConnectionOrOptions);
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete account data from database');
    }

    _set({ isPersisting: false, processingState: 'complete', persistenceError: null });

    const preserveConn = typeof preserveConnectionOrOptions === 'boolean'
      ? preserveConnectionOrOptions
      : !!preserveConnectionOrOptions?.preserveConnection;

    if (!preserveConn) {
      try {
        const { clearWealthicaStorePersistence } = await import('@/utils/zustandPersistenceCleanup');
        clearWealthicaStorePersistence();
      } catch (persistenceError) {
        console.warn('💾 PERSISTENCE: Failed to clear Zustand persistence after deletion', { persistenceError });
      }
    }

    try {
      const options = typeof preserveConnectionOrOptions === 'object' ? preserveConnectionOrOptions : undefined;
      const suppressSuccessToast = options?.suppressSuccessToast === true;
      if (!suppressSuccessToast) {
        const resultRec = asRecord(result);
        const detailsRec = asRecord(resultRec.details);
        const dataRec = asRecord(detailsRec.data ?? detailsRec);
        const deleteMode = typeof dataRec.deleteMode === 'string' ? dataRec.deleteMode : 'full_delete';

        if (deleteMode === 'stop_syncing') {
          const remainingConnections = getNumber(dataRec.remainingConnections);
          toast.success('Stopped syncing connection', {
            description: `Connections remaining: ${remainingConnections ?? 'unknown'}`,
            duration: 4000,
          });
        } else {
          const connectionsDeleted = getNumber(dataRec.connectionsDeleted) ?? 0;
          const connectionPreserved = connectionsDeleted === 0;
          if (connectionPreserved) {
            toast.success('Data persistence disabled', {
              description: 'Connection preserved for view-only access.',
              duration: 4000,
            });
          } else {
            toast.success('Data removed', {
              description: `Connections removed: ${connectionsDeleted}`,
              duration: 4000,
            });
          }
        }
      }
    } catch {
      try { toast.success('Data removed'); } catch { /* noop */ }
    }

    try {
      window.dispatchEvent(new CustomEvent('wealthica:persist:deleted', {
        detail: { userId, institutionId: connectionData?.institutionId, timestamp: Date.now() }
      }));
    } catch { /* noop */ }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown deletion error';
    console.error('💾 PERSISTENCE: Failed to delete from database', { error: errorMessage });
    _set({ isPersisting: false, processingState: 'error', persistenceError: errorMessage });
    throw error;
  }
}
